import { inject, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { STRRate, STRAmount, STRCalcSubCatAmountDto, STRData, GenericTimeSlot, ShortTermRentalDateTime, STRResult } from '../../../../models';
import { startDateBeforeEndDateValidator } from '../../../../validators/start-date-is-before-end-date.validator';
import { distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppStateService, ShortTermRentalCheckoutService, ShortTermRentalService } from '../../../../services';
import { CheckoutTabNames, DayOfWeek, PaymentType, STRInterval } from '../../../../enums';

/** when extending this class be sure to implement ngOnInit */
export abstract class NgxAimRentalCheckAvailabilityClass {
  // ngOnInit() {
  //   this.init();
  // }

  fb = inject(FormBuilder);
  rentalService = inject(ShortTermRentalService);
  checkoutService = inject(ShortTermRentalCheckoutService);
  appState = inject(AppStateService);

  form = this.fb.group({
    startDate: this.fb.control<Date | null>({ value: null, disabled: true }, { validators: [Validators.required] }),
    endDate: this.fb.control<Date | null>({ value: null, disabled: true }, { validators: [Validators.required] }),
    rate: this.fb.control<STRRate | null>(null, { validators: [Validators.required] }),
    returnDate: this.fb.control<string | null>({ value: null, disabled: true }),
  }, { validators: [startDateBeforeEndDateValidator] });

  changes$ = this.form.valueChanges.pipe(
    distinctUntilChanged(),
    takeUntilDestroyed(),
  );

  rentalData = this.checkoutService.getRentalData();
  rates: STRRate[] = [];
  amount?: STRAmount | null;
  minStartDate = new Date();
  minEndDate = new Date();
  endDefaultDate = new Date();
  startTimeSlots: ShortTermRentalDateTime[] = [];
  startTimeSlot: GenericTimeSlot | undefined;
  endTimeSlots: ShortTermRentalDateTime[] = [];
  endTimeSlot: GenericTimeSlot | undefined;
  startDateText = signal('Pickup');
  endDateText = signal('Return');
  actionButtonText: 'CHECK AVAILABILITY' | 'CONTINUE' = 'CHECK AVAILABILITY';
  error = '';
  amountError = '';
  isLoadingRates = false;
  isLoadingAmount = false;
  isLoadingTimeSlots = false;
  showTime = false;
  startTimeDisabled = true;
  endTimeDisabled = true;
  acceptPayments = true;
  isEndTimeSelected = false;

  init() {
    this.getRates();
    this.initValueChanges();
    this.initDateText();
    this.acceptPayments = this.appState.paymentType !== PaymentType.NONE;
    this.minStartDate.setHours(0);
    this.minStartDate.setMinutes(0);
  }

  getRates() {
    this.isLoadingRates = true;
    this.form.controls.startDate.enable();
    this.form.controls.endDate.enable();
    this.rentalService.getRateForSubCat(this.rentalData.subCategory).subscribe({
      next: (res) => this.onGetRatesSuccess(res),
      error: (err) => {
        this.isLoadingRates = false;
        this.error = 'Please call for pricing';
      }
    });
  }

  onGetRatesSuccess(res: STRResult) {
    this.isLoadingRates = false;
    this.rates = res.Rates;
    this.form.controls.startDate.enable();
    this.form.controls.endDate.enable();

    if (this.rates.length === 1) {
      this.form.controls.rate.setValue(this.rates[0]);
      this.form.controls.rate.disable();
    }
  }

  initDateText() {
    if (this.rentalData.subCategory.startDateText && this.rentalData.subCategory.endDateText) {
      this.startDateText.set(this.rentalData.subCategory.startDateText);
      this.endDateText.set(this.rentalData.subCategory.endDateText);
    }
  }

  getTimeSlots(dayOfWeek: DayOfWeek, property: 'start' | 'end') {
    if (this.rentalData.subCategory.isLodging) {
      this.getLodgingTimeSlots(dayOfWeek, property);
      return;
    }

    this.isLoadingTimeSlots = true;
    this.rentalService.getTimeSlots(dayOfWeek).subscribe({
      next: (timeSlots) => {
        if (property === 'start') {
          this.startTimeSlots = timeSlots;
        }
        if (property === 'end') {
          this.endTimeSlots = timeSlots;
        }

        this.isLoadingTimeSlots = false;
      },
      error: (err) => {
        this.error = 'Please call for pricing';
        this.isLoadingTimeSlots = false;
      }
    });
  }

  getLodgingTimeSlots(dayOfWeek: DayOfWeek, property: 'start' | 'end') {
    if (property === 'start') {
      if (!this.rentalData.subCategory[`day${dayOfWeek}CheckInTime`]) {
        alert(`day ${dayOfWeek} has no check-in time`);
        return;
      }

      this.startTimeSlots = [{
        id: -1,
        dayOfWeek: dayOfWeek,
        time: this.rentalData.subCategory[`day${dayOfWeek}CheckInTime`]!,
        displayTime: this.rentalData.subCategory[`day${dayOfWeek}CheckInDisplayTime`]!,
        enabled: true,
        date: this.form.controls.startDate.value!,
        HeaderDesc: '',
        HeaderNotes: '',
        Desc: '',
        IntervalType: STRInterval.DAYS,
        Notes: '',
        Amt: 0,
        TimeInterval: 1,
      }];
    }

    if (property === 'end') {
      if (!this.rentalData.subCategory[`day${dayOfWeek}CheckOutTime`]) {
        alert(`day ${dayOfWeek} has no check-out time`);
        return;
      }

      this.endTimeSlots = [{
        id: -1,
        dayOfWeek: dayOfWeek,
        time: this.rentalData.subCategory[`day${dayOfWeek}CheckOutTime`]!,
        displayTime: this.rentalData.subCategory[`day${dayOfWeek}CheckOutDisplayTime`]!,
        enabled: true,
        date: this.form.controls.endDate.value!,
        HeaderDesc: '',
        HeaderNotes: '',
        Desc: '',
        IntervalType: STRInterval.DAYS,
        Notes: '',
        Amt: 0,
        TimeInterval: 1,
      }];
    }
  }

  initValueChanges() {
    this.changes$.subscribe((res) => {
      if (this.actionButtonText !== 'CHECK AVAILABILITY') {
        this.actionButtonText = 'CHECK AVAILABILITY';
        this.amount = null;
      }

      this.showTime = res.rate?.IntervalType === 'Hours' ? true : false;

      if (this.form.controls.startDate.value) {
        this.startTimeDisabled = false;
      } else {
        this.startTimeDisabled = true;
      }

      if (this.form.controls.endDate.value) {
        this.endTimeDisabled = false;
      } else {
        this.endTimeDisabled = true;
      }
    });
  }

  calcAmount() {
    if (!this.form.controls.startDate.value || !this.form.controls.endDate.value) {
      return;
    }

    this.isLoadingAmount = true;
    this.amountError = '';
    this.amount = null;

    if (this.form.controls.rate.value?.IntervalType !== STRInterval.HOURS && !this.rentalData.subCategory.isLodging) {
      this.onTimeChange({ id: -1, dayOfWeek: DayOfWeek.SUNDAY, time: '00:00', displayTime: '12:00 AM', enabled: true }, 'start');
      this.onTimeChange({ id: -1, dayOfWeek: DayOfWeek.SUNDAY, time: '23:45', displayTime: '11:45 PM', enabled: true }, 'end');
    }

    if (this.rentalData.subCategory.isLodging) {
      if (!this.startTimeSlots.length) {
        alert('No available start time slots');
        this.isLoadingAmount = false;
        return;
      }

      if (!this.endTimeSlots.length) {
        alert('No available end time slots');
        this.isLoadingAmount = false;
        return;
      }

      this.onTimeChange({ id: -1, dayOfWeek: DayOfWeek.SUNDAY, time: this.startTimeSlots[0].time, displayTime: this.startTimeSlots[0].displayTime, enabled: true }, 'start');
      this.onTimeChange({ id: -1, dayOfWeek: DayOfWeek.SUNDAY, time: this.endTimeSlots[0].time, displayTime: this.endTimeSlots[0].displayTime, enabled: true }, 'end');
    }

    const dto: STRCalcSubCatAmountDto = {
      CategoryNumber: this.rentalData.CategoryNumber,
      SubNumber: this.rentalData.SubNumber,
      strData: {
        rate: this.form.controls.rate.value!,
        pickupDateString: this.form.controls.startDate.value.toLocaleDateString('sv'),
        pickupTime: this.startTimeSlot!,
        returnDateString: this.form.controls.endDate.value.toLocaleDateString('sv'),
        returnTime: this.endTimeSlot!,
      }
    }

    const now = new Date();
    now.setMinutes(now.getMinutes() + 16);
    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: this.appState.businessInfo.tz,
    });
    const nowFormattedTime = formatter.format(now);

    // If the normal check in time has already pasted and they select the current day add 16 minutes to the current
    // time in the timezone of the business to get through the checkout process. Aim will not accept a date in the past.
    if (
      this.rentalData.subCategory.isLodging
      && +dto.strData.pickupTime.time.split(':')[0] < +nowFormattedTime.split(':')[0]
      && this.form.controls.startDate.value.toLocaleDateString('sv') === now.toLocaleDateString('sv')
    ) {
      dto.strData.pickupTime.time = nowFormattedTime;
    }

    this.rentalService.calcAmountForSubCat(dto).subscribe({
      next: (res) => this.onCalcSuccess(res, dto.strData),
      error: (err) => this.onCalcFailure(err),
    });
  }

  onCalcSuccess(res: STRAmount, strData: STRData) {
    this.actionButtonText = 'CONTINUE';
    this.isLoadingAmount = false;
    this.amount = res;
    strData.strAmount = res;
    this.checkoutService.setStrData(strData);
  }

  onCalcFailure(err: any) {
    this.actionButtonText = 'CHECK AVAILABILITY';
    this.amount = null;
    this.isLoadingAmount = false;
    this.amountError = err.error.message;
  }

  onActionButtonClick() {
    if (this.actionButtonText === 'CHECK AVAILABILITY') {
      this.calcAmount();
      return;
    }

    if (this.actionButtonText === 'CONTINUE') {
      this.checkoutService.setTabIsDisabled(CheckoutTabNames.BILLING_ADDRESS, false);
      this.checkoutService.setTabIndexByHeader(CheckoutTabNames.BILLING_ADDRESS);
    }
  }

  onDateSelected(type: 'start' | 'end') {
    if (type === 'start') {
      this.getTimeSlots(this.form.controls.startDate.value!.getDay(), 'start');
      this.endDefaultDate = new Date(this.form.controls.startDate.value!);

      if (this.rentalData.subCategory.isLodging) {
        this.minEndDate.setDate(this.form.controls.startDate.value!.getDate() + 1);
      } else {
        this.minEndDate.setDate(this.form.controls.startDate.value!.getDate());
      }
    }

    if (type === 'end') {
      this.getTimeSlots(this.form.controls.endDate.value!.getDay(), 'end');
    }
  }

  onTimeChange(timeSlot: GenericTimeSlot, type: 'start' | 'end') {
    this.actionButtonText = 'CHECK AVAILABILITY';
    this.amount = null;
    this.amountError = '';

    if (type === 'start') {
      this.startTimeSlot = timeSlot;
      const timeParts = timeSlot.time.split(':');
      const startDate = new Date(this.form.controls.startDate.value!);
      startDate.setHours(+timeParts[0]);
      startDate.setMinutes(+timeParts[1]);
      this.form.controls.startDate.setValue(startDate, { emitEvent: false });
      if (this.form.controls.rate.value?.IntervalType === 'Hours' && this.form.controls.rate.value.TimeInterval > 1) {
        const endDate = new Date(this.form.controls.startDate.value!);
        endDate.setHours(endDate.getHours() + this.form.controls.rate.value.TimeInterval);
        this.form.controls.endDate.setValue(endDate, { emitEvent: false });
        this.form.controls.returnDate.setValue(`${endDate.toLocaleDateString()} - ${endDate.toLocaleTimeString()}`);

        const time = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
        this.endTimeSlot = { id: -1, dayOfWeek: DayOfWeek.SUNDAY, time: time, displayTime: '', enabled: true }
      }
    }

    if (type === 'end') {
      this.endTimeSlot = timeSlot;
      const timeParts = timeSlot.time.split(':');
      const endDate = new Date(this.form.controls.endDate.value!);
      endDate.setHours(+timeParts[0]);
      endDate.setMinutes(+timeParts[1]);
      if (!this.rentalData.subCategory.isLodging) {
        this.form.controls.endDate.setValue(endDate, { emitEvent: false });
      }
      this.isEndTimeSelected = true;
    }
  }

  onRateChange() {
    this.form.controls.startDate.setValue(null);
    this.form.controls.endDate.setValue(null);
    this.form.controls.startDate.markAsPristine();
    this.form.controls.startDate.markAsUntouched();
    this.form.controls.endDate.markAsPristine();
    this.form.controls.endDate.markAsUntouched();
    this.isEndTimeSelected = false;
  }

  getIntervalString(IntervalType: string) {
    if (this.rentalData.subCategory.isLodging) {
      if (IntervalType === 'Day') {
        return 'Night';
      } else {
        return 'Nights';
      }
    } else {
      return IntervalType;
    }
  }

  get fc() { return this.form.controls; }
}
